import type { SymbolComponent } from '@tsagko/label-contracts'
import { validateGtinCheckDigit } from '@tsagko/label-contracts'
import { resolveValueSource } from '../internal/units'

/**
 * ITF-14 helpers (issue #129).
 *
 * ITF-14 is GS1's Interleaved-2-of-5 carrier for GTIN-14 — the carton/
 * logistics-unit identifier. Unlike EAN-13 (`barcodes/ean-13.ts`), where the
 * PRINTER computes and appends the 13th check digit, ITF-14 here transmits
 * the FULL 14 digits (including the GS1 mod-10 check digit) and asks the
 * printer NOT to recompute one (`^B2`'s check-digit option `e=N` — see
 * `serialize.ts`). We validate the supplied check digit ourselves, reusing
 * the length-agnostic `validateGtinCheckDigit` from
 * `@tsagko/label-contracts` (GTIN-12/13/14/SSCC-18 share the same mod-10
 * algorithm — only the payload length differs).
 *
 * This asymmetry with EAN-13 is a deliberate DECIDED choice (not a
 * mirroring oversight): ^B2's check-digit auto-append interacts with ITF's
 * "odd digit count gets a leading zero inserted by the printer" rule in a
 * way that was not practical to probe-verify here (see recon notes on
 * render-129-130) — transmitting the complete, self-validated 14-digit
 * payload sidesteps that ambiguity entirely.
 *
 * These helpers are pure and stateless, mirroring the shape of
 * `derivePayloadForEan13`/`validateEan13` so the four-symbology (now five)
 * family in `barcodes/` stays structurally symmetric.
 */

export type Itf14ValidationResult = { ok: true } | { ok: false; reason: string }

/**
 * Validate that `value` is a well-formed 14-digit ITF-14/GTIN-14: exactly 14
 * ASCII digits AND the trailing digit matches the GS1 mod-10 check digit
 * computed over the first 13. Returns a discriminated result rather than
 * throwing so callers (`lintTemplate`) can present the failure as a warning.
 */
export function validateItf14(value: string): Itf14ValidationResult {
  if (value.length !== 14) {
    return {
      ok: false,
      reason: `ITF-14 must be exactly 14 characters; got length ${value.length}`,
    }
  }
  if (!/^\d{14}$/.test(value)) {
    return { ok: false, reason: 'ITF-14 must contain only ASCII digits 0-9' }
  }
  return validateGtinCheckDigit(value)
}

/**
 * Build the ITF-14 payload string from a `SymbolComponent`.
 *
 *   - `gtin`   → resolve the ValueSource, strip non-digits, emit at most 14
 *                digits.
 *   - `raw`    → resolve, strip non-digits, emit at most 14 digits (identical
 *                to `gtin` here; length/check-digit mismatches surface as
 *                lint warnings, not here).
 *   - `gs1-ai` → `undefined` (silent skip — semantically nonsensical for
 *                ITF-14; GS1-128 is the right symbology for AI-structured
 *                data).
 *   - `url`    → `undefined` (silent skip — URLs are not ITF-14 payloads).
 *
 * Returns `undefined` to signal "skip this primitive" to the caller.
 *
 * Unlike `derivePayloadForEan13` (which drops the check digit and lets the
 * printer compute it), this deriver keeps ALL digits up to 14 — the check
 * digit is part of the transmitted payload, not appended by the printer
 * (see the module docstring). A short input is passed through unpadded; a
 * long one is trimmed to the first 14. Neither case is validated here —
 * that is `validateItf14`'s job, called from `lintTemplate`.
 */
export function derivePayloadForItf14(
  component: SymbolComponent,
  data: Record<string, unknown>
): string | undefined {
  const structure = component.dataStructure
  switch (structure.kind) {
    case 'gtin':
    case 'raw': {
      const resolved = resolveValueSource(structure.value, data)
      const digits = resolved.replace(/\D/g, '')
      return digits.length > 14 ? digits.slice(0, 14) : digits
    }
    case 'gs1-ai':
    case 'url':
      // Nonsensical for ITF-14 — silent skip. Lint flags the mismatch.
      return undefined
    default: {
      const _exhaustive: never = structure
      throw new Error(
        `derivePayloadForItf14: unknown structure ${JSON.stringify(_exhaustive)}`
      )
    }
  }
}
